/**
 * DataChannel Protocol - Binary message format for WebRTC communication
 * Must match the C# DataChannelProtocol in WebRTCModels.cs
 *
 * Wire format:
 *   Wrapper: [Type:1][Length:4 LE][Payload:N]   (HeaderSize = 5)
 *   Frame payload: [Width:4 LE][Height:4 LE][Codec:1][KeyFrame:1][Data:N]
 */

import { DataChannelMessageType, FrameData, VideoCodec } from '../types';

// Wrapper header: [Type:1 byte][Length:4 bytes little-endian] = 5 bytes
// Matches C# DataChannelProtocol.HeaderSize = 5
const WRAPPER_HEADER_SIZE = 5;

// Frame payload layout (offsets within payload, after wrapper extraction):
// [Width:4][Height:4][Codec:1][KeyFrame:1][Data:N] = 10 bytes metadata
// Matches C# CreateFrameMessage / ParseFrameMessage
const FRAME_METADATA_SIZE = 10;
const WIDTH_OFFSET = 0;
const HEIGHT_OFFSET = 4;
const CODEC_OFFSET = 8;
const KEYFRAME_OFFSET = 9;
const DATA_OFFSET = 10;

export class DataChannelProtocol {
    /**
     * Parse a received binary message from the DataChannel.
     * Wire format: [Type:1][Length:4 LE][Payload:N]
     */
    static parseMessage(data: ArrayBuffer): { type: DataChannelMessageType; payload: Uint8Array } | null {
        if (data.byteLength < WRAPPER_HEADER_SIZE) {
            return null;
        }

        const view = new DataView(data);
        const type = view.getUint8(0) as DataChannelMessageType;
        const length = view.getInt32(1, true); // little-endian, matches C# BitConverter

        if (length < 0 || data.byteLength < WRAPPER_HEADER_SIZE + length) {
            console.warn(`Invalid DataChannel message: declared length ${length}, buffer size ${data.byteLength}`);
            return null;
        }

        const payload = new Uint8Array(data, WRAPPER_HEADER_SIZE, length);
        return { type, payload };
    }

    /**
     * Parse a frame message payload and extract frame data.
     * Payload format: [Width:4 LE][Height:4 LE][Codec:1][KeyFrame:1][Data:N]
     */
    static parseFrameMessage(payload: Uint8Array): FrameData | null {
        if (payload.length < FRAME_METADATA_SIZE) {
            console.warn('Frame message too short:', payload.length);
            return null;
        }

        const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);

        const width = view.getInt32(WIDTH_OFFSET, true);      // bytes 0-3
        const height = view.getInt32(HEIGHT_OFFSET, true);     // bytes 4-7
        const codec = view.getUint8(CODEC_OFFSET) as VideoCodec; // byte 8 (1 byte, not 4!)
        const isKeyFrame = view.getUint8(KEYFRAME_OFFSET) !== 0; // byte 9
        const frameData = payload.slice(DATA_OFFSET);              // bytes 10+

        // Validate frame dimensions (BUG-5)
        if (width <= 0 || height <= 0 || width > 7680 || height > 4320) {
            console.warn(`Invalid frame dimensions: ${width}x${height}`);
            return null;
        }
        if (codec > 8) {
            console.warn(`Invalid codec value: ${codec}`);
            return null;
        }

        return {
            data: frameData,
            width,
            height,
            codec,
            isKeyFrame,
            frameNumber: 0, // Will be set by caller if needed
            timestamp: performance.now()
        };
    }

    /**
     * Create a binary message for sending.
     * Wire format: [Type:1][Length:4 LE][Payload:N]
     */
    static createMessage(type: DataChannelMessageType, payload: Uint8Array): Uint8Array {
        const message = new Uint8Array(WRAPPER_HEADER_SIZE + payload.length);
        const view = new DataView(message.buffer);
        view.setUint8(0, type);
        view.setInt32(1, payload.length, true); // little-endian length
        message.set(payload, WRAPPER_HEADER_SIZE);
        return message;
    }

    /**
     * Create an input command message
     */
    static createInputMessage(inputJson: string): Uint8Array {
        const encoder = new TextEncoder();
        const jsonBytes = encoder.encode(inputJson);
        return this.createMessage(DataChannelMessageType.Input, jsonBytes);
    }

    /**
     * Create a ping message with timestamp
     */
    static createPingMessage(): Uint8Array {
        const timestamp = BigInt(Date.now());
        const payload = new Uint8Array(8);
        const view = new DataView(payload.buffer);
        view.setBigInt64(0, timestamp, true);
        return this.createMessage(DataChannelMessageType.Ping, payload);
    }

    /**
     * Create a pong response message
     */
    static createPongMessage(pingPayload: Uint8Array): Uint8Array {
        return this.createMessage(DataChannelMessageType.Pong, pingPayload);
    }

    /**
     * Parse a pong message and calculate RTT
     */
    static parsePongMessage(payload: Uint8Array): number {
        if (payload.length < 8) {
            return 0;
        }

        const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
        const sentTimestamp = Number(view.getBigInt64(0, true));
        const rtt = Date.now() - sentTimestamp;
        return rtt;
    }

    /**
     * Get human-readable message type name
     */
    static getMessageTypeName(type: DataChannelMessageType): string {
        switch (type) {
            case DataChannelMessageType.Frame: return 'Frame';
            case DataChannelMessageType.Input: return 'Input';
            case DataChannelMessageType.Audio: return 'Audio';
            case DataChannelMessageType.FileChunk: return 'FileChunk';
            case DataChannelMessageType.Clipboard: return 'Clipboard';
            case DataChannelMessageType.Ping: return 'Ping';
            case DataChannelMessageType.Pong: return 'Pong';
            case DataChannelMessageType.Stats: return 'Stats';
            default: return `Unknown(${type})`;
        }
    }
}
